import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// Dialog: shadcn/ui's Dialog, restyled as the kit's solid panel over a midnight scrim. Focus is
// trapped while it is open and Tab wraps inside it; Escape or a click on the scrim closes it (a
// press that begins and ends on the scrim: not a drag from the scrim onto the panel or back); focus
// returns to the trigger on close.
//
// The surface's own Root, Trigger and Content over Radix's DismissableLayer (Escape, and a click
// on the scrim, which closes it once the press has ended), not Radix's Dialog: that one writes focus guards into document.body, aria-hidden on the
// body's other children, a scroll lock on <head> and <body>, and pointer-events on <body>, whatever
// its container (src/ui/layer.ts says why that matters). Here the dialog is aria-modal, the rest of
// the container it opens in is aria-hidden while it is open (inside a Surface root, the rest of the
// surface; never the page around it), and the scrim, not a scroll lock, keeps the wheel off what is
// behind.
//
// Content renders in a portal: the container passed, else the Surface root (ui/portal.ts), else
// document.body.
import { DismissableLayer } from "@radix-ui/react-dismissable-layer";
import { Slot } from "@radix-ui/react-slot";
import { hideOthers } from "aria-hidden";
import { cva } from "class-variance-authority";
import { createContext, useContext, useEffect, useId, useMemo, useRef, useState, } from "react";
import { createPortal } from "react-dom";
import { t } from "../i18n/t.js";
import { IconButton } from "./button.js";
import { cn } from "./cn.js";
import { compose, composeRefs, useFocusScope, useLayerPart, useOpenState, } from "./layer.js";
import { usePortalContainer } from "./portal.js";
const DialogContext = createContext(null);
function useDialog(part) {
    const ctx = useContext(DialogContext);
    if (ctx === null)
        throw new Error(`${part} must be inside a Dialog or a Sheet`);
    return ctx;
}
export function Dialog({ children, ...state }) {
    const [open, setOpen] = useOpenState(state);
    const id = useId();
    const trigger = useRef(null);
    const value = useMemo(() => ({
        open,
        setOpen,
        contentId: `${id}-content`,
        titleId: `${id}-title`,
        descriptionId: `${id}-description`,
        trigger,
    }), [open, setOpen, id]);
    return _jsx(DialogContext, { value: value, children: children });
}
export function DialogTrigger({ asChild = false, ref, onClick, ...props }) {
    const ctx = useDialog("DialogTrigger");
    const Comp = asChild ? Slot : "button";
    return (_jsx(Comp, { type: "button", "aria-haspopup": "dialog", "aria-expanded": ctx.open, "aria-controls": ctx.open ? ctx.contentId : undefined, "data-state": ctx.open ? "open" : "closed", ...props, ref: composeRefs(ref, ctx.trigger), onClick: compose(onClick, () => {
            ctx.setOpen(!ctx.open);
        }) }));
}
export function DialogClose({ asChild = false, onClick, ...props }) {
    const { setOpen } = useDialog("DialogClose");
    const Comp = asChild ? Slot : "button";
    return (_jsx(Comp, { type: "button", ...props, onClick: compose(onClick, () => {
            setOpen(false);
        }) }));
}
/** Wheel and touch scrolling on the scrim go nowhere: what is behind stays where it is. */
function holdScroll(node) {
    if (node === null)
        return undefined;
    const stop = (event) => {
        event.preventDefault();
    };
    node.addEventListener("wheel", stop, { passive: false });
    node.addEventListener("touchmove", stop, { passive: false });
    return () => {
        node.removeEventListener("wheel", stop);
        node.removeEventListener("touchmove", stop);
    };
}
/** The scrim behind a dialog or sheet. */
export function DialogOverlay({ className, ref, ...props }) {
    const [node, setNode] = useState(null);
    const overlayRef = useMemo(() => composeRefs(ref, setNode), [ref]);
    useEffect(() => holdScroll(node), [node]);
    return (_jsx("div", { "data-state": "open", className: cn("pointer-events-auto fixed inset-0 z-modal bg-br-midnight/60", className), ...props, ref: overlayRef, 
        // A press on the scrim does not take focus out of the dialog (any button).
        onMouseDown: compose(props.onMouseDown, (event) => {
            event.preventDefault();
        }) }));
}
/** The panel every dialog and sheet shares: solid midnight panel, strong hairline, deep shadow. */
export const panelClasses = "pointer-events-auto z-modal flex flex-col overscroll-contain border-wp-line-strong bg-wp-bg-2 font-sans text-14 leading-body text-wp-text shadow-3 outline-none";
/** The portal, the scrim and the focus-trapped panel, shared by DialogContent and SheetContent. */
export function ModalContent({ container, ...props }) {
    const ctx = useDialog("DialogContent");
    const target = usePortalContainer(container);
    const [scrim, setScrim] = useState(null);
    useLayerPart(scrim);
    const scrimPress = useRef(null);
    if (!ctx.open || target === null)
        return null;
    return createPortal(_jsxs(_Fragment, { children: [_jsx(DialogOverlay, { ref: setScrim, onPointerDown: (event) => {
                    scrimPress.current = { down: event.nativeEvent, up: false };
                }, onPointerUp: () => {
                    if (scrimPress.current !== null)
                        scrimPress.current.up = true;
                } }), _jsx(ModalLayer, { ctx: ctx, target: target, scrimPress: scrimPress, ...props })] }), target);
}
function ModalLayer({ ctx, target, scrimPress, onKeyDown, ref, ...props }) {
    const [content, setContent] = useState(null);
    const contentRef = useMemo(() => composeRefs(ref, setContent), [ref]);
    useLayerPart(content);
    const tab = useFocusScope(content, {
        trapped: true,
        onUnmount: (opener) => {
            (ctx.trigger.current ?? opener)?.focus({ preventScroll: true });
        },
    });
    // Everything else in the container is hidden from assistive technology while this is open.
    useEffect(() => (content === null ? undefined : hideOthers(content, target)), [content, target]);
    return (_jsx(DismissableLayer, { asChild: true, 
        // A press on the scrim closes the dialog on the click that ends it, not on pointerdown: the
        // whole press (mousedown, mouseup, click; on touch, the mouse events after touchend) lands
        // on the scrim, so none of it reaches what is under it, and focus goes back to the trigger
        // after the press rather than to <body> with the mousedown that would follow.
        deferPointerDownOutside: true, 
        // Only a plain primary press closes it. DismissableLayer defers only button 0, so any other
        // button (or ctrl+click, a right-click on a Mac) would close it on pointerdown and let the
        // rest of the press reach the page; as Radix's modal dialog does, those leave it open.
        // And only a press that began and ended on the scrim: the click after a drag from the scrim
        // onto the panel fires on their common ancestor, outside the panel, and would close it
        // (selecting the panel's text from its edge, say); a drag from the panel onto the scrim began
        // inside, so DismissableLayer never counts it. So a scrim click needs the scrim's pointerup to
        // reach the surface as well as its pointerdown: a page that stops pointerup in a window or
        // document capture listener keeps the dialog open on a scrim click (Escape and × still close
        // it).
        onPointerDownOutside: (event) => {
            const press = event.detail.originalEvent;
            const onScrim = scrimPress.current;
            const scrimOnly = onScrim !== null && onScrim.down === press && onScrim.up;
            if (press.button !== 0 || press.ctrlKey || !scrimOnly)
                event.preventDefault();
        }, onFocusOutside: (event) => {
            event.preventDefault(); // the focus scope pulls focus back instead
        }, onDismiss: () => {
            ctx.setOpen(false);
        }, children: _jsx("div", { role: "dialog", "aria-modal": "true", id: ctx.contentId, "aria-labelledby": ctx.titleId, "aria-describedby": ctx.descriptionId, "data-state": "open", tabIndex: -1, ...props, ref: contentRef, onKeyDown: compose(onKeyDown, tab) }) }));
}
const dialogContentVariants = cva([
    panelClasses,
    "fixed top-1/2 left-1/2 w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-16 border",
], {
    variants: {
        compact: { false: "max-w-md gap-3 p-5", true: "max-w-sm gap-2 p-4" },
    },
    defaultVariants: { compact: false },
});
/** The close button's glyph until the icon set (E09-S1-T3) supplies one. */
export const PLACEHOLDER_CLOSE_GLYPH = "×";
export function DialogContent({ className, compact, closeGlyph = PLACEHOLDER_CLOSE_GLYPH, children, ...props }) {
    return (_jsxs(ModalContent, { "data-compact": compact ?? false, className: cn(dialogContentVariants({ compact }), className), ...props, children: [children, closeGlyph === null ? null : _jsx(CloseButton, { glyph: closeGlyph, compact: compact })] }));
}
/** The corner close control shared by Dialog and Sheet. */
export function CloseButton({ glyph, compact }) {
    return (_jsx(DialogClose, { asChild: true, children: _jsx(IconButton, { label: t("common.close"), icon: glyph, compact: compact, className: compact ? "absolute top-2 right-2" : "absolute top-3 right-3" }) }));
}
export function DialogHeader({ className, ...props }) {
    return _jsx("div", { className: cn("flex flex-col gap-1 pr-8", className), ...props });
}
export function DialogFooter({ className, ...props }) {
    return (_jsx("div", { className: cn("flex flex-wrap items-center justify-end gap-2", className), ...props }));
}
export function DialogTitle({ className, ...props }) {
    const { titleId } = useDialog("DialogTitle");
    return (_jsx("h2", { id: titleId, className: cn("text-15 leading-tight font-semibold", className), ...props }));
}
export function DialogDescription({ className, ...props }) {
    const { descriptionId } = useDialog("DialogDescription");
    return (_jsx("p", { id: descriptionId, className: cn("text-12 leading-tight text-wp-text-3", className), ...props }));
}
//# sourceMappingURL=dialog.js.map